import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import Header from '../components/Header';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Loader2, Plus, Trash2, Mail, User, KeyRound, Download } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getRoleStyle = (role) => {
  if (role === 'admin') return { avatar: 'bg-yellow-400', badge: 'bg-yellow-100 text-yellow-700' };
  if (role === 'interno') return { avatar: 'bg-emerald-500', badge: 'bg-emerald-100 text-emerald-700' };
  return { avatar: 'bg-blue-500', badge: 'bg-blue-100 text-blue-700' };
};

export default function AdminUsersPage() {
  const { t } = useLanguage();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newUserOpen, setNewUserOpen] = useState(false);
  const [newUser, setNewUser] = useState({ first_name: '', last_name: '', email: '', password: '', phone: '', role: 'coworker' });
  const [creatingUser, setCreatingUser] = useState(false);
  const [resetPwUser, setResetPwUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [resettingPw, setResettingPw] = useState(false);
  const [exporting, setExporting] = useState(false);

  const fetchUsers = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/admin/users`, { withCredentials: true });
      setUsers(data);
    } catch {
      toast.error(t('error'));
    }
  }, [t]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      await fetchUsers();
      setLoading(false);
    };
    load();
  }, [fetchUsers]);

  const handleResetPassword = async () => {
    if (!resetPwUser || newPassword.length < 6) return;
    setResettingPw(true);
    try {
      await axios.put(`${API_URL}/api/admin/users/${resetPwUser.id}/password`,
        { new_password: newPassword }, { withCredentials: true });
      toast.success(t('passwordReset'));
      setResetPwUser(null);
      setNewPassword('');
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('error'));
    } finally {
      setResettingPw(false);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setCreatingUser(true);
    try {
      await axios.post(`${API_URL}/api/admin/users`, newUser, { withCredentials: true });
      toast.success(t('userCreatedSuccess'));
      setNewUser({ first_name: '', last_name: '', email: '', password: '', phone: '', role: 'coworker' });
      setNewUserOpen(false);
      await fetchUsers();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('error'));
    } finally {
      setCreatingUser(false);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm(t('confirmDeleteUser'))) return;
    try {
      await axios.delete(`${API_URL}/api/admin/users/${userId}`, { withCredentials: true });
      toast.success(t('userDeleted'));
      await fetchUsers();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('error'));
    }
  };

  const handleToggleUserRole = async (userId, currentRole) => {
    const roles = ['coworker', 'interno', 'admin'];
    const currentIdx = roles.indexOf(currentRole);
    const newRole = roles[(currentIdx + 1) % roles.length];
    try {
      await axios.put(`${API_URL}/api/admin/users/${userId}/role`, { role: newRole }, { withCredentials: true });
      toast.success(t('roleUpdated'));
      await fetchUsers();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('error'));
    }
  };

  const handleExportDb = async () => {
    setExporting(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/admin/export-db`, { withCredentials: true });
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `cowork_desk_export_${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(t('exportSuccess'));
    } catch {
      toast.error(t('error'));
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header />
        <div className="flex items-center justify-center h-[calc(100vh-100px)]">
          <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-users-page">
      <Header />
      <main className="max-w-7xl mx-auto px-4 py-8">
        <Card className="card-floating">
          <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-3">
            <CardTitle className="text-xl font-extrabold text-slate-900">
              {t('userManagement')}
            </CardTitle>
            <div className="flex items-center gap-2">
              <Button
                onClick={handleExportDb}
                disabled={exporting}
                variant="outline"
                size="sm"
                data-testid="export-db-btn"
                className="rounded-full text-xs"
              >
                {exporting ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Download className="w-3 h-3 mr-1" />}
                {exporting ? t('exporting') : t('exportDb')}
              </Button>
              <Dialog open={newUserOpen} onOpenChange={setNewUserOpen}>
                <DialogTrigger asChild>
                  <Button data-testid="add-user-btn" className="btn-primary">
                    <Plus className="w-4 h-4 mr-2" />
                    {t('newUser')}
                  </Button>
                </DialogTrigger>
                <DialogContent className="rounded-3xl">
                  <DialogHeader>
                    <DialogTitle className="text-xl font-extrabold">
                      {t('createNewUser')}
                    </DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleCreateUser} className="space-y-4 mt-4">
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-2">
                        <Label htmlFor="firstName" className="font-semibold">{t('firstName')}</Label>
                        <Input
                          id="firstName"
                          data-testid="new-user-first-name"
                          value={newUser.first_name}
                          onChange={(e) => setNewUser({ ...newUser, first_name: e.target.value })}
                          placeholder={t('firstNamePlaceholder')}
                          className="rounded-xl"
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="lastName" className="font-semibold">{t('lastName')}</Label>
                        <Input
                          id="lastName"
                          data-testid="new-user-last-name"
                          value={newUser.last_name}
                          onChange={(e) => setNewUser({ ...newUser, last_name: e.target.value })}
                          placeholder={t('lastNamePlaceholder')}
                          className="rounded-xl"
                          required
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email" className="font-semibold">{t('email')}</Label>
                      <Input
                        id="email"
                        type="email"
                        data-testid="new-user-email"
                        value={newUser.email}
                        onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                        placeholder={t('adminEmailPlaceholder')}
                        className="rounded-xl"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="phone" className="font-semibold">{t('phone')}</Label>
                      <Input
                        id="phone"
                        type="tel"
                        data-testid="new-user-phone"
                        value={newUser.phone}
                        onChange={(e) => setNewUser({ ...newUser, phone: e.target.value })}
                        placeholder={t('phonePlaceholder')}
                        className="rounded-xl"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="password" className="font-semibold">{t('password')}</Label>
                      <Input
                        id="password"
                        type="password"
                        data-testid="new-user-password"
                        value={newUser.password}
                        onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                        placeholder={t('adminPasswordPlaceholder')}
                        className="rounded-xl"
                        required
                        minLength={6}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="font-semibold">{t('role')}</Label>
                      <div className="flex gap-2">
                        {['coworker', 'interno', 'admin'].map((role) => (
                          <button
                            key={role}
                            type="button"
                            onClick={() => setNewUser({ ...newUser, role })}
                            data-testid={`new-user-role-${role}`}
                            className={`flex-1 py-2 px-3 rounded-xl text-sm font-bold transition-all ${
                              newUser.role === role
                                ? role === 'admin' ? 'bg-yellow-400 text-yellow-900'
                                  : role === 'interno' ? 'bg-emerald-500 text-white'
                                  : 'bg-blue-500 text-white'
                                : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                            }`}
                          >
                            {t(`role${role.charAt(0).toUpperCase() + role.slice(1)}`)}
                          </button>
                        ))}
                      </div>
                    </div>
                    <Button
                      type="submit"
                      data-testid="create-user-submit"
                      disabled={creatingUser}
                      className="w-full btn-primary"
                    >
                      {creatingUser ? <Loader2 className="w-4 h-4 animate-spin" /> : t('createUser')}
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
          </CardHeader>
          <CardContent>
            {users.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-slate-500 font-medium">{t('noUsers')}</p>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {users.map((u) => (
                  <div
                    key={u.id}
                    data-testid={`user-${u.id}`}
                    className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-2xl p-3 md:p-4"
                  >
                    <div className="flex items-start gap-2 md:gap-3">
                      <div className={`w-8 h-8 md:w-10 md:h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${getRoleStyle(u.role).avatar}`}>
                        <User className="w-4 h-4 md:w-5 md:h-5 text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-bold text-slate-900 text-sm md:text-base truncate">{u.name}</p>
                        <p className="text-xs md:text-sm text-slate-500 flex items-center gap-1 truncate">
                          <Mail className="w-3 h-3 flex-shrink-0" />
                          <span className="truncate">{u.email}</span>
                        </p>
                      </div>
                    </div>
                    <div className="mt-2 md:mt-3 flex items-center justify-between">
                      <button
                        onClick={() => handleToggleUserRole(u.id, u.role)}
                        data-testid={`user-role-btn-${u.id}`}
                        className={`text-xs px-3 py-1 rounded-full font-bold cursor-pointer transition-all hover:opacity-80 ${getRoleStyle(u.role).badge}`}
                      >
                        {t(`role${u.role.charAt(0).toUpperCase() + u.role.slice(1)}`)}
                      </button>
                      <div className="flex items-center">
                        <Button
                          variant="ghost"
                          size="icon"
                          data-testid={`reset-pw-${u.id}`}
                          onClick={() => { setResetPwUser(u); setNewPassword(''); }}
                          className="text-amber-500 hover:text-amber-700 hover:bg-amber-50 rounded-full h-8 w-8"
                          title={t('resetPassword')}
                        >
                          <KeyRound className="w-3.5 h-3.5" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          data-testid={`delete-user-${u.id}`}
                          onClick={() => handleDeleteUser(u.id)}
                          className="text-rose-500 hover:text-rose-700 hover:bg-rose-50 rounded-full h-8 w-8"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Reset Password Dialog */}
        <Dialog open={!!resetPwUser} onOpenChange={(open) => { if (!open) setResetPwUser(null); }}>
          <DialogContent className="rounded-3xl" aria-describedby="reset-pw-desc">
            <DialogHeader>
              <DialogTitle className="text-lg font-extrabold flex items-center gap-2">
                <KeyRound className="w-5 h-5 text-amber-500" />
                {t('resetPassword')}
              </DialogTitle>
            </DialogHeader>
            <p id="reset-pw-desc" className="text-sm text-slate-500">
              {resetPwUser?.name} ({resetPwUser?.email})
            </p>
            <div className="space-y-4 mt-2">
              <div className="space-y-2">
                <Label className="font-semibold">{t('newPassword')}</Label>
                <Input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder={t('adminPasswordPlaceholder')}
                  className="rounded-xl"
                  minLength={6}
                  data-testid="reset-pw-input"
                />
              </div>
              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setResetPwUser(null)} className="flex-1 rounded-full">
                  {t('cancel')}
                </Button>
                <Button
                  onClick={handleResetPassword}
                  disabled={resettingPw || newPassword.length < 6}
                  data-testid="reset-pw-submit"
                  className="flex-1 bg-amber-500 hover:bg-amber-600 text-white rounded-full font-bold"
                >
                  {resettingPw ? <Loader2 className="w-4 h-4 animate-spin" /> : t('confirm')}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
